import type { AIResponse, HistoryList } from "@/types";

export interface PendingChatParams {
  topicNo?: string;
  conversationNo: string;
  question: string;
}

interface CreatePendingChatBootstrapInput {
  currentTopicNo?: string;
  pendingParams: PendingChatParams | null;
  pendingQuestion: string | null;
  historyRecords?: Pick<HistoryList, "conversationNo" | "query">[];
}

interface ShouldShowChatViewInput {
  currentTopicNo?: string;
  messageCount: number;
  historyLength: number;
  isLoading: boolean;
}

interface ShouldClearPendingChatContextInput {
  currentTopicNo?: string;
  pendingParams: PendingChatParams | null;
  hasExistingRecord: boolean;
  pollCompleted: boolean;
}

export function getPendingChatParams(rawParams: string | null): PendingChatParams | null {
  if (!rawParams) return null;

  try {
    const parsed = JSON.parse(rawParams) as Partial<PendingChatParams>;

    if (typeof parsed.conversationNo !== "string" || !parsed.conversationNo) {
      return null;
    }

    if (typeof parsed.question !== "string" || !parsed.question.trim()) {
      return null;
    }

    return {
      topicNo: typeof parsed.topicNo === "string" ? parsed.topicNo : undefined,
      conversationNo: parsed.conversationNo,
      question: parsed.question,
    };
  } catch {
    return null;
  }
}

export function createPendingChatBootstrap({
  currentTopicNo,
  pendingParams,
  pendingQuestion,
  historyRecords = [],
}: CreatePendingChatBootstrapInput): AIResponse | null {
  if (!pendingParams || !pendingQuestion?.trim()) return null;
  if (pendingParams.topicNo && currentTopicNo && pendingParams.topicNo !== currentTopicNo) return null;

  const existingRecord = historyRecords.find(
    (record) =>
      record.conversationNo === pendingParams.conversationNo &&
      record.query === pendingQuestion
  );
  if (existingRecord) return null;

  return {
    conversationNo: pendingParams.conversationNo,
    events: [],
    question: pendingQuestion,
    isOpen: true,
  };
}

export function shouldShowChatView({
  currentTopicNo,
  messageCount,
  historyLength,
  isLoading,
}: ShouldShowChatViewInput) {
  return Boolean(currentTopicNo || messageCount > 0 || historyLength > 0 || isLoading);
}

export function shouldClearPendingChatContext({
  currentTopicNo,
  pendingParams,
  hasExistingRecord,
  pollCompleted,
}: ShouldClearPendingChatContextInput) {
  if (!pendingParams) return false;
  if (pendingParams.topicNo && currentTopicNo && pendingParams.topicNo !== currentTopicNo) return false;

  return hasExistingRecord || pollCompleted;
}
